import React, { createContext, useContext, useCallback, useEffect, useMemo, useRef, ReactNode } from 'react';
import { subscriptionService, SubscriptionStatus } from '../services/subscriptionService';
import { checkTrialStatus, startTrial as startTrialAPI } from '../services/trialService';
import { useAuth } from './AuthContext';
import { logger } from '../utils/logger';
import { SUBSCRIPTION_ENABLED } from '../config/monetization';

// =============================================================================
// TYPES & INTERFACES - Designed for Future Expansion
// =============================================================================

export interface TrialState {
  trialStartDate: Date | null;
  trialEndDate: Date | null;
  isTrialActive: boolean;
  isTrialExpired: boolean;
  daysRemaining: number;
  isPremiumUser: boolean;
  hasSeenWelcome: boolean;
}

// Future expansion: Different subscription tiers
export type SubscriptionTier = 'free' | 'basic' | 'premium' | 'pro';

// Future expansion: Feature access control
export interface FeatureFlags {
  // Current features
  fiftyFourDayNovena: boolean;
  basicProgressTracking: boolean;
  intentionSetting: boolean;
  
  // Future prayer types
  divineNovena?: boolean;
  sacredHeartNovena?: boolean;
  stJosephNovena?: boolean;
  customNovenas?: boolean;
  
  // Future premium features
  analyticsAndInsights?: boolean;
  customReminders?: boolean;
  prayerStreaks?: boolean;
  communityFeatures?: boolean;
  offlineMode?: boolean;
  exportData?: boolean;
  prioritySupport?: boolean;
  
  // Future integrations
  calendarSync?: boolean;
  shareProgress?: boolean;
  prayerJournal?: boolean;
}

export interface SubscriptionState extends TrialState {
  // Current subscription info
  subscriptionTier: SubscriptionTier;
  features: FeatureFlags;

  // Database subscription status
  databaseStatus?: SubscriptionStatus;
  isLoadingDatabase?: boolean;

  // Future expansion: subscription details
  subscriptionId?: string;
  customerId?: string;
  currentPeriodEnd?: Date;
  cancelAtPeriodEnd?: boolean;

  // Usage analytics (future)
  lastActiveDate?: Date;
  totalPrayersCompleted?: number;
  currentStreak?: number;
  longestStreak?: number;
}

// Actions for state management
interface SubscriptionActions {
  // Trial management
  startTrial: () => void;
  activatePremium: () => void;
  deactivatePremium: () => void;
  markWelcomeSeen: () => void;
  clearTrialData: () => void;

  // Future actions
  upgradeTier?: (tier: SubscriptionTier) => void;
  cancelSubscription?: () => void;
  reactivateSubscription?: () => void;
  updateFeatures?: (features: Partial<FeatureFlags>) => void;

  // Utility functions
  hasFeature: (feature: keyof FeatureFlags) => boolean;
  getSubscriptionStatusMessage: () => string;
  refreshSubscriptionState: () => void;
}

// Computed properties interface (backward compatibility)
interface SubscriptionComputedProperties {
  hasAccess: boolean;
  hasTrialBeenUsed: boolean;
}

export type SubscriptionContextType = SubscriptionState & SubscriptionActions & SubscriptionComputedProperties;

// =============================================================================
// CONSTANTS - Easy to modify for expansion
// =============================================================================

const STORAGE_KEYS = {
  TRIAL_DATA: 'novena-trial-data',
  PREMIUM_STATUS: 'novena-premium-status',
  WELCOME_SEEN: 'novena-welcome-seen',
  SUBSCRIPTION_DATA: 'novena-subscription-data', // Future use
} as const;

// Feature definitions by subscription tier
const TIER_FEATURES: Record<SubscriptionTier, FeatureFlags> = {
  free: {
    fiftyFourDayNovena: false, // Requires trial/subscription
    basicProgressTracking: false,
    intentionSetting: false,
  },
  basic: { // Trial users
    fiftyFourDayNovena: true,
    basicProgressTracking: true,
    intentionSetting: true,
  },
  premium: { // Current paid tier
    fiftyFourDayNovena: true,
    basicProgressTracking: true,
    intentionSetting: true,
    // Future features would be enabled here
    divineNovena: true,
    sacredHeartNovena: true,
    analyticsAndInsights: true,
    customReminders: true,
    prayerStreaks: true,
  },
  pro: { // Future higher tier
    fiftyFourDayNovena: true,
    basicProgressTracking: true,
    intentionSetting: true,
    divineNovena: true,
    sacredHeartNovena: true,
    stJosephNovena: true,
    customNovenas: true,
    analyticsAndInsights: true,
    customReminders: true,
    prayerStreaks: true,
    communityFeatures: true,
    offlineMode: true,
    exportData: true,
    prioritySupport: true,
    calendarSync: true,
    shareProgress: true,
    prayerJournal: true,
  },
};

// =============================================================================
// CONTEXT CREATION
// =============================================================================

const SubscriptionContext = createContext<SubscriptionContextType | null>(null);

// =============================================================================
// PROVIDER COMPONENT
// =============================================================================

interface SubscriptionProviderProps {
  children: ReactNode;
}

export const SubscriptionProvider: React.FC<SubscriptionProviderProps> = ({ children }) => {
  const auth = useAuth();
  const [subscriptionState, setSubscriptionState] = React.useState<SubscriptionState>(() =>
    initializeSubscriptionState()
  );
  const [databaseStatus, setDatabaseStatus] = React.useState<SubscriptionStatus | null>(null);
  const [isLoadingDatabase, setIsLoadingDatabase] = React.useState(false);

  // Refs to store interval IDs for proper cleanup
  const trialStatusIntervalRef = useRef<NodeJS.Timeout | null>(null);
  const refreshIntervalRef = useRef<NodeJS.Timeout | null>(null);

  // =============================================================================
  // STATE INITIALIZATION
  // =============================================================================

  function initializeSubscriptionState(): SubscriptionState {
    let trialData: string | null = null;
    let premiumStatus: string | null = null;
    let hasSeenWelcome = false;
    
    try {
      trialData = localStorage.getItem(STORAGE_KEYS.TRIAL_DATA);
      premiumStatus = localStorage.getItem(STORAGE_KEYS.PREMIUM_STATUS);
      hasSeenWelcome = localStorage.getItem(STORAGE_KEYS.WELCOME_SEEN) === 'true';
    } catch (error) {
      logger.warn('Failed to access localStorage during subscription state initialization', error);
    }
    
    // Debug logging for development
    logger.subscription.debug('Raw subscription data loaded', {
      hasTrialData: !!trialData,
      hasPremiumStatus: !!premiumStatus,
      hasSeenWelcome
    });
    
    let trialStartDate: Date | null = null;
    let trialEndDate: Date | null = null;
    
    if (trialData) {
      try {
        const parsed = JSON.parse(trialData);
        trialStartDate = parsed.trialStartDate ? new Date(parsed.trialStartDate) : null;
        trialEndDate = parsed.trialEndDate ? new Date(parsed.trialEndDate) : null;
      } catch (error) {
        logger.warn('Failed to parse trial data', error);
      }
    }
    
    const isPremiumUser = premiumStatus === 'true';
    const now = new Date();
    
    const isTrialActive = Boolean(
      trialStartDate && 
      trialEndDate && 
      now >= trialStartDate && 
      now <= trialEndDate &&
      !isPremiumUser
    );
    
    const isTrialExpired = Boolean(
      trialStartDate && 
      trialEndDate && 
      now > trialEndDate &&
      !isPremiumUser
    );
    
    const daysRemaining = trialEndDate
      ? Math.max(0, Math.ceil((trialEndDate.getTime() - now.getTime()) / (1000 * 60 * 60 * 24)))
      : 0;

    // Determine subscription tier
    let subscriptionTier: SubscriptionTier = 'free';
    if (isPremiumUser) {
      subscriptionTier = 'premium';
    } else if (isTrialActive) {
      subscriptionTier = 'basic';
    }

    const result: SubscriptionState = {
      // Trial state
      trialStartDate,
      trialEndDate,
      isTrialActive,
      isTrialExpired,
      daysRemaining,
      isPremiumUser,
      hasSeenWelcome,
      
      // New subscription features
      subscriptionTier,
      features: TIER_FEATURES[subscriptionTier],
      
      // Future expansion ready
      lastActiveDate: new Date(),
    };
    
    // Debug logging for development
    logger.subscription.debug('Computed subscription state', result);
    
    return result;
  }

  // =============================================================================
  // ACTION HANDLERS
  // =============================================================================

  const refreshSubscriptionState = useCallback(() => {
    setSubscriptionState(initializeSubscriptionState());
  }, []);

  // =============================================================================
  // SERVER TRIAL STATUS - Fetch from backend on mount and auth changes
  // =============================================================================
  useEffect(() => {
    const fetchServerTrialStatus = async () => {
      if (!auth.isAuthenticated || !auth.user) {
        return;
      }

      try {
        logger.subscription.debug('Fetching trial status from server');

        const status = await checkTrialStatus(
          auth.user.email,
          auth.user.id
        );

        logger.subscription.debug('Server trial status received', status);

        // Update subscription state with server data
        setSubscriptionState(prev => ({
          ...prev,
          trialStartDate: status.trialStartedAt ? new Date(status.trialStartedAt) : null,
          trialEndDate: status.trialExpiresAt ? new Date(status.trialExpiresAt) : null,
          isTrialActive: status.isTrialActive || false,
          isTrialExpired: status.isTrialExpired || false,
          daysRemaining: status.daysRemaining || 0,
          isPremiumUser: status.isPremiumUser || false,
          subscriptionTier: status.isPremiumUser
            ? 'premium'
            : (status.isTrialActive ? 'basic' : 'free'),
          features: status.isPremiumUser
            ? TIER_FEATURES.premium
            : (status.isTrialActive ? TIER_FEATURES.basic : TIER_FEATURES.free)
        }));

      } catch (error) {
        logger.error('Failed to fetch server trial status', error);
      }
    };

    fetchServerTrialStatus();

    // Refresh every 30 minutes while app is open (industry standard for prayer/meditation apps)
    // Clear any existing interval first to prevent duplicates
    if (trialStatusIntervalRef.current) {
      clearInterval(trialStatusIntervalRef.current);
    }
    trialStatusIntervalRef.current = setInterval(fetchServerTrialStatus, 30 * 60 * 1000);

    return () => {
      if (trialStatusIntervalRef.current) {
        clearInterval(trialStatusIntervalRef.current);
        trialStatusIntervalRef.current = null;
      }
    };

  }, [auth.isAuthenticated, auth.user]);

  // Check database subscription status
  const checkDatabaseStatus = useCallback(async () => {
    // Only check database for authenticated users
    if (!auth.isAuthenticated || !auth.user) {
      setDatabaseStatus(null);
      setIsLoadingDatabase(false);
      return;
    }

    setIsLoadingDatabase(true);
    try {
      const options = {
        googleId: auth.user.id,
        email: auth.user.email
      };

      const status = await subscriptionService.checkSubscriptionStatus(options);
      setDatabaseStatus(status);

      logger.subscription.debug('Database subscription status loaded', {
        hasAccess: status.hasAccess,
        accessType: status.accessType,
        checkedAt: status.checkedAt
      });
    } catch (error) {
      logger.subscription.debug('Failed to check database subscription status', error);
      // Set default no-access state on error
      setDatabaseStatus({
        hasAccess: false,
        accessType: 'none',
        trialEligible: true,
        checkedAt: new Date().toISOString()
      });
    } finally {
      setIsLoadingDatabase(false);
    }
  }, [auth.isAuthenticated, auth.user]);

  const startTrial = useCallback(async () => {
    if (!auth.user) {
      logger.error('Cannot start trial: user not authenticated');
      return;
    }

    try {
      logger.subscription.debug('Starting trial via server API');

      // Call server API instead of localStorage
      const response = await startTrialAPI(
        auth.user.email,
        auth.user.id,
        subscriptionService.getDeviceId()
      );

      logger.subscription.trialStarted();
      logger.subscription.debug('Trial started successfully', response);

      // Refresh trial status immediately from server
      const status = await checkTrialStatus(auth.user.email, auth.user.id);

      // Update state with fresh server data
      setSubscriptionState(prev => ({
        ...prev,
        trialStartDate: status.trialStartedAt ? new Date(status.trialStartedAt) : null,
        trialEndDate: status.trialExpiresAt ? new Date(status.trialExpiresAt) : null,
        isTrialActive: status.isTrialActive || false,
        isTrialExpired: status.isTrialExpired || false,
        daysRemaining: status.daysRemaining || 0,
        isPremiumUser: status.isPremiumUser || false,
        subscriptionTier: 'basic',
        features: TIER_FEATURES.basic
      }));

    } catch (error) {
      logger.error('Failed to start trial', error);
      throw error;
    }
  }, [auth.user]);

  const activatePremium = useCallback(() => {
    try {
      localStorage.setItem(STORAGE_KEYS.PREMIUM_STATUS, 'true');
      refreshSubscriptionState();
      
      logger.subscription.premiumActivated();
    } catch (error) {
      console.error('Failed to activate premium:', error);
    }
  }, [refreshSubscriptionState]);

  const deactivatePremium = useCallback(() => {
    try {
      localStorage.removeItem(STORAGE_KEYS.PREMIUM_STATUS);
      refreshSubscriptionState();
    } catch (error) {
      console.error('Failed to deactivate premium:', error);
    }
  }, [refreshSubscriptionState]);

  const markWelcomeSeen = useCallback(() => {
    try {
      localStorage.setItem(STORAGE_KEYS.WELCOME_SEEN, 'true');
      refreshSubscriptionState();
    } catch (error) {
      console.error('Failed to mark welcome seen:', error);
    }
  }, [refreshSubscriptionState]);

  const clearTrialData = useCallback(() => {
    try {
      localStorage.removeItem(STORAGE_KEYS.TRIAL_DATA);
      localStorage.removeItem(STORAGE_KEYS.PREMIUM_STATUS);
      localStorage.removeItem(STORAGE_KEYS.WELCOME_SEEN);
      refreshSubscriptionState();
    } catch (error) {
      console.error('Failed to clear trial data:', error);
    }
  }, [refreshSubscriptionState]);

  // =============================================================================
  // UTILITY FUNCTIONS
  // =============================================================================

  const hasFeature = useCallback((feature: keyof FeatureFlags): boolean => {
    return Boolean(subscriptionState.features[feature]);
  }, [subscriptionState.features]);

  const getSubscriptionStatusMessage = useCallback(() => {
    if (subscriptionState.isPremiumUser) {
      return 'Premium subscription active';
    }

    if (subscriptionState.isTrialActive) {
      const days = subscriptionState.daysRemaining;

      // Normal trial messaging
      if (days === 1) {
        return 'Last day of your free trial';
      } else if (days === 0) {
        return 'Trial expires today';
      } else {
        return `${days} days left in your free trial`;
      }
    }

    if (subscriptionState.isTrialExpired) {
      return 'Free trial has expired';
    }

    return 'Start your 7-day free trial';
  }, [subscriptionState]);

  // =============================================================================
  // COMPUTED VALUES
  // =============================================================================

  const hasAccess = useMemo(() => {
    // Freemium mode: every feature is accessible when the subscription system is off.
    if (!SUBSCRIPTION_ENABLED) {
      return true;
    }

    // Local trial/premium state takes precedence (whether authenticated or not)
    const localAccess = subscriptionState.isPremiumUser || subscriptionState.isTrialActive;

    // For authenticated users: Use database status only if no local access
    if (auth.isAuthenticated && databaseStatus && !localAccess) {
      return databaseStatus.hasAccess;
    }

    // Default to local state (trial or premium)
    return localAccess;
  }, [auth.isAuthenticated, databaseStatus, subscriptionState.isPremiumUser, subscriptionState.isTrialActive]);

  const hasTrialBeenUsed = useMemo(() => {
    // A trial has been used if there's any trial start date recorded
    return subscriptionState.trialStartDate !== null;
  }, [subscriptionState.trialStartDate]);

  // =============================================================================
  // CONTEXT VALUE
  // =============================================================================

  const contextValue: SubscriptionContextType = {
    // State
    ...subscriptionState,
    databaseStatus: databaseStatus || undefined,
    isLoadingDatabase,

    // Computed values (for backward compatibility)
    hasAccess,
    hasTrialBeenUsed,

    // Actions
    startTrial,
    activatePremium,
    deactivatePremium,
    markWelcomeSeen,
    clearTrialData,
    hasFeature,
    getSubscriptionStatusMessage: getSubscriptionStatusMessage,
    refreshSubscriptionState,
  };

  // =============================================================================
  // DATABASE STATUS CHECKING
  // =============================================================================

  // Check database status on mount
  useEffect(() => {
    checkDatabaseStatus();
  }, [checkDatabaseStatus]);

  // =============================================================================
  // AUTO-REFRESH TIMER - Reduced frequency to avoid excessive API calls
  // =============================================================================

  useEffect(() => {
    // Clear any existing interval first to prevent duplicates
    if (refreshIntervalRef.current) {
      clearInterval(refreshIntervalRef.current);
    }

    // Check every 60 minutes (industry standard for subscription apps)
    // Trial status is already checked every 30 minutes via fetchServerTrialStatus
    refreshIntervalRef.current = setInterval(() => {
      refreshSubscriptionState();
      checkDatabaseStatus();
    }, 60 * 60 * 1000); // 60 minutes

    return () => {
      if (refreshIntervalRef.current) {
        clearInterval(refreshIntervalRef.current);
        refreshIntervalRef.current = null;
      }
    };
  }, [refreshSubscriptionState, checkDatabaseStatus]);

  return (
    <SubscriptionContext.Provider value={contextValue}>
      {children}
    </SubscriptionContext.Provider>
  );
};

// =============================================================================
// HOOKS FOR CONSUMPTION
// =============================================================================

export const useSubscription = (): SubscriptionContextType => {
  const context = useContext(SubscriptionContext);
  if (!context) {
    throw new Error('useSubscription must be used within a SubscriptionProvider');
  }
  return context;
};

// Legacy compatibility hook - same API as old useTrialState
export const useTrialState = () => {
  const subscription = useSubscription();
  
  return {
    // Original API maintained for backward compatibility
    trialStartDate: subscription.trialStartDate,
    trialEndDate: subscription.trialEndDate,
    isTrialActive: subscription.isTrialActive,
    isTrialExpired: subscription.isTrialExpired,
    daysRemaining: subscription.daysRemaining,
    isPremiumUser: subscription.isPremiumUser,
    hasSeenWelcome: subscription.hasSeenWelcome,
    hasAccess: subscription.hasAccess,
    
    // Actions
    startTrial: subscription.startTrial,
    activatePremium: subscription.activatePremium,
    deactivatePremium: subscription.deactivatePremium,
    markWelcomeSeen: subscription.markWelcomeSeen,
    clearTrialData: subscription.clearTrialData,
    refreshTrialState: subscription.refreshSubscriptionState,
    getTrialStatusMessage: subscription.getSubscriptionStatusMessage,
  };
};

export default SubscriptionContext;